import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'motion/react';
import { Icon } from './Icon.js';
import type { IconName } from './Icon.js';
import { shouldCloseContextMenuOnSwipeRight } from '../lib/sidebarInteractions.js';

export interface MenuItem {
  key: string;
  label: string;
  icon?: IconName;
  danger?: boolean;
  onSelect: () => void;
}

export interface ContextMenuProps {
  open: boolean;
  x: number;
  y: number;
  items?: MenuItem[];
  children?: ReactNode;
  className?: string;
  /** `right`: x is the menu's right edge instead of its left edge. */
  align?: 'left' | 'right';
  /** Place the menu next to this rect (below it, or above when there is no room). */
  anchorRect?: { top: number; bottom: number; left: number; right: number } | null;
  /** Dim and blur the page behind the menu. */
  blur?: boolean;
  onClose: () => void;
}

/** How long the post-close click swallow may linger when no click ever follows. */
const CLICK_SWALLOW_FALLBACK_MS = 300;

/** Lightweight popover menu anchored to a point, clamped to the viewport. */
export function ContextMenu({
  open,
  x,
  y,
  items = [],
  children,
  className,
  align = 'left',
  anchorRect = null,
  blur = false,
  onClose,
}: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);
  const [pos, setPos] = useState({ left: x, top: y });
  const [origin, setOrigin] = useState('top left');
  // Guard period after opening: ignore pointer events so the finger that
  // triggered the long-press cannot accidentally activate a menu item.
  const [interactive, setInteractive] = useState(false);

  useLayoutEffect(() => {
    if (!open) return;
    const el = menuRef.current;
    if (!el) return;
    // offsetWidth/Height ignore the entry scale animation (getBoundingClientRect would not).
    const rect = { width: el.offsetWidth, height: el.offsetHeight };
    const margin = 8;
    const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
    const vv = window.visualViewport;
    const viewportRight = Math.min(
      window.innerWidth,
      document.documentElement.clientWidth || window.innerWidth,
      vv ? vv.offsetLeft + vv.width : Infinity,
    );
    const viewportBottom = vv
      ? Math.min(window.innerHeight, vv.offsetTop + vv.height)
      : window.innerHeight;
    // When inside the mobile sidebar drawer, clamp within the sidebar's own
    // boundaries so the menu never bleeds into the main chat area.
    const sidebar = document.querySelector<HTMLElement>('.sidebar.is-open');
    const sidebarRect = sidebar?.getBoundingClientRect();
    const minLeft = sidebarRect ? sidebarRect.left + margin : margin;
    const maxLeft = (sidebarRect ? sidebarRect.right : viewportRight) - rect.width - margin;
    const minTop = sidebarRect ? sidebarRect.top + margin : margin;
    const maxTop = (sidebarRect ? sidebarRect.bottom : viewportBottom) - rect.height - margin;
    if (anchorRect) {
      const gap = 8;
      const wantLeft = align === 'right' ? anchorRect.right - rect.width : anchorRect.left;
      const below = anchorRect.bottom + gap;
      const fitsBelow = below + rect.height <= viewportBottom - margin;
      const top = fitsBelow ? below : Math.max(minTop, anchorRect.top - gap - rect.height);
      const left = clamp(wantLeft, minLeft, Math.max(minLeft, maxLeft));
      setPos({ left, top: clamp(top, minTop, Math.max(minTop, maxTop)) });
      setOrigin(`${fitsBelow ? 'top' : 'bottom'} ${align === 'right' ? 'right' : 'left'}`);
      return;
    }
    setPos({
      left: clamp(align === 'right' ? x - rect.width : x, minLeft, Math.max(minLeft, maxLeft)),
      top: clamp(y, minTop, Math.max(minTop, maxTop)),
    });
    setOrigin(`top ${align === 'right' ? 'right' : 'left'}`);
  }, [open, x, y, align, anchorRect]);

  useEffect(() => {
    if (!open) {
      setInteractive(false);
      return;
    }
    // Allow interactions after 150 ms – imperceptible for deliberate taps but
    // prevents accidental activation from the long-press release.
    const timer = window.setTimeout(() => setInteractive(true), 150);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Do NOT auto-focus the first item on open: on touch devices a programmatic
  // .focus() immediately after the long-press triggers a visible focus-ring on
  // the top item (e.g. "Rename") that the user did not intentionally select.
  // Keyboard users can navigate with arrow keys once the menu is interactive.

  if (!open) return null;

  const handleOverlayPointerDown = (e: React.PointerEvent) => {
    e.stopPropagation();
    // Register the swallow listener BEFORE calling onClose to guarantee it's
    // in place before any synchronous re-render triggers a new click event.
    // A touch that turns into a scroll / swipe never produces a click, so the
    // listener is also dropped shortly after the pointer is released (and by a
    // timeout fallback) — otherwise it would eat the next tap anywhere.
    let releaseTimer: number | undefined;
    const cleanup = () => {
      window.clearTimeout(releaseTimer);
      window.clearTimeout(fallbackTimer);
      document.removeEventListener('click', swallow, { capture: true });
      document.removeEventListener('pointerup', onRelease);
      document.removeEventListener('pointercancel', onRelease);
    };
    const swallow = (ev: MouseEvent) => {
      ev.stopPropagation();
      cleanup();
    };
    // The click follows pointerup in the same gesture: give it a moment to arrive.
    const onRelease = () => {
      releaseTimer = window.setTimeout(cleanup, 50);
    };
    const fallbackTimer = window.setTimeout(cleanup, CLICK_SWALLOW_FALLBACK_MS);
    document.addEventListener('click', swallow, { capture: true });
    document.addEventListener('pointerup', onRelease);
    document.addEventListener('pointercancel', onRelease);
    onClose();
  };

  const handleOverlayTouchStart = (event: React.TouchEvent) => {
    const touch = event.touches[0];
    if (!touch) return;
    swipeStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleOverlayTouchMove = (event: React.TouchEvent) => {
    const start = swipeStartRef.current;
    const touch = event.touches[0];
    if (!start || !touch) return;
    const horizontalDistance = touch.clientX - start.x;
    const verticalDistance = Math.abs(touch.clientY - start.y);
    if (shouldCloseContextMenuOnSwipeRight(horizontalDistance, verticalDistance)) {
      swipeStartRef.current = null;
      onClose();
    }
  };

  return createPortal(
    <div
      className={`menu-overlay${blur ? ' menu-overlay-blur' : ''}`}
      onPointerDown={handleOverlayPointerDown}
      onTouchStart={handleOverlayTouchStart}
      onTouchMove={handleOverlayTouchMove}
      onTouchEnd={() => {
        swipeStartRef.current = null;
      }}
      onTouchCancel={() => {
        swipeStartRef.current = null;
      }}
    >
      <motion.div
        ref={menuRef}
        className={`context-menu${className ? ` ${className}` : ''}`}
        role="menu"
        style={{ left: pos.left, top: pos.top, transformOrigin: origin, pointerEvents: interactive ? 'auto' : 'none' }}
        onPointerDown={(event) => event.stopPropagation()}
        initial={{ opacity: 0, scale: blur ? 0.55 : 0.92, y: blur ? 0 : -4 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92, y: -4 }}
        transition={blur
          ? { type: 'spring', stiffness: 520, damping: 28, mass: 0.8 }
          : { type: 'spring', bounce: 0.2, duration: 0.28 }}
        onKeyDown={(event) => {
          if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
          const items = Array.from(
            menuRef.current?.querySelectorAll<HTMLButtonElement>('.menu-item') ?? [],
          );
          if (items.length === 0) return;
          event.preventDefault();
          const activeIndex = items.findIndex((item) => item === document.activeElement);
          if (event.key === 'Home') {
            items[0]?.focus();
            return;
          }
          if (event.key === 'End') {
            items.at(-1)?.focus();
            return;
          }
          const delta = event.key === 'ArrowDown' ? 1 : -1;
          if (activeIndex === -1 && event.key === 'ArrowDown') {
            items[0]?.focus();
            return;
          }
          if (activeIndex === -1 && event.key === 'ArrowUp') {
            items.at(-1)?.focus();
            return;
          }
          const startIndex = activeIndex;
          const nextIndex = (startIndex + delta + items.length) % items.length;
          items[nextIndex]?.focus();
        }}
      >
        {children ??
          items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              tabIndex={-1}
              className={`menu-item${item.danger ? ' menu-item-danger' : ''}`}
              onClick={() => {
                item.onSelect();
                onClose();
              }}
            >
              {item.icon && <Icon name={item.icon} size={18} />}
              <span>{item.label}</span>
            </button>
          ))}
      </motion.div>
    </div>,
    document.body,
  );
}
